import React, { FC } from 'react';
import {
  Card,
  CardContent,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { PageStatusResult } from '../pageStatusTypes';
import { getDisplayUrl, getEffectiveUrlDisplay, getStatusChipColor, getStatusLabel } from '../pageStatusUtils';
import { PageStatusResponseTimeBadge } from './PageStatusResponseTimeBadge';
import { PageStatusResultUrlCell } from './PageStatusResultUrlCell';
import { PageStatusSectionTitle } from './PageStatusSectionTitle';

type PageStatusCompactResultTableProps = {
  title: string;
  helper?: string;
  results: PageStatusResult[];
  includeDetails?: boolean;
};

export const PageStatusCompactResultTable: FC<PageStatusCompactResultTableProps> = ({
  title,
  helper,
  results,
  includeDetails = false,
}) => {
  if (results.length === 0) {
    return null;
  }

  return (
    <Card variant='outlined' sx={{ mb: 3, borderRadius: 2, maxWidth: '100%', overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <PageStatusSectionTitle title={title} helper={helper} />

        <TableContainer
          component={Paper}
          variant='outlined'
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
          }}>
          <Table size='small' sx={{ minWidth: { xs: includeDetails ? 620 : 520, sm: 650 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: { xs: 86, sm: 110 } }}>Status</TableCell>
                <TableCell sx={{ minWidth: { xs: 230, sm: 320 } }}>URL</TableCell>
                {includeDetails && <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Detaljer</TableCell>}
                <TableCell align='right' sx={{ width: { xs: 116, sm: 140 } }}>
                  Responstid
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {results.map((result) => {
                const details = result.error || getEffectiveUrlDisplay(result);

                return (
                  <TableRow
                    key={`${title}-${getDisplayUrl(result)}-${result.status_code}-${result.response_time}`}
                    hover>
                    <TableCell sx={{ width: { xs: 86, sm: 110 } }}>
                      <Chip
                        label={getStatusLabel(result.status_code)}
                        color={getStatusChipColor(result.status_code)}
                        size='small'
                        variant='outlined'
                      />
                    </TableCell>

                    <TableCell sx={{ minWidth: { xs: 230, sm: 320 } }}>
                      <PageStatusResultUrlCell result={result} />
                    </TableCell>

                    {includeDetails && (
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, maxWidth: 420 }}>
                        <Typography
                          variant='body2'
                          color='text.primary'
                          sx={{
                            overflowWrap: 'anywhere',
                          }}>
                          {details || '-'}
                        </Typography>
                      </TableCell>
                    )}

                    <TableCell align='right' sx={{ width: { xs: 116, sm: 140 } }}>
                      <PageStatusResponseTimeBadge responseTime={result.response_time} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};
